import { LockOutlined, SafetyCertificateFilled } from "@ant-design/icons";
import { Button, Modal, Tag, Tooltip, message } from "antd";
import { useAppDispatch, useAppSelector } from "hooks";
import React from "react";
import { demoteSchemaRequest, promoteSchemaRequest } from "store/schemaPromotion/actions";

// PRE-2379 — schema promotion. A PERMANENT schema is the immutable master definition:
// it cannot be deleted (backend answers 423 Locked) and its promoted revision is tagged
// "V{promotedVersion} PERMANENT" while editing continues on the mutable head.

interface ISchemaPromotionActionProps {
    businessAreaId?: string;
    schemaId?: string;
    schemaName?: string;
    promotionStatus?: string;
    promotedVersion?: number | null;
}

export default function SchemaPromotionAction({ businessAreaId, schemaId, schemaName, promotionStatus, promotedVersion }: ISchemaPromotionActionProps) {
    const dispatch = useAppDispatch();
    const override = useAppSelector((s) => s.schemaPromotion?.promotionBySchema?.[schemaId || ""]);
    const working = useAppSelector((s) => s.schemaPromotion?.working);
    const promotionResult = useAppSelector((s) => s.schemaPromotion?.promotionResult);
    const wasWorking = React.useRef(false);

    // In-session override wins over the fetched schema model.
    const effectiveStatus = override?.promotionStatus ?? promotionStatus ?? "VOLATILE";
    const effectiveVersion = override?.promotedVersion ?? promotedVersion ?? null;

    React.useEffect(() => {
        if (wasWorking.current && !working && promotionResult && !promotionResult.success) {
            message.error(promotionResult.message || "Promotion change failed.");
        }
        wasWorking.current = !!working;
    }, [working, promotionResult]);

    if (!schemaId) {
        return null;
    }

    if (effectiveStatus === "PERMANENT") {
        return (
            <span className="inline-flex items-center gap-2" data-testid="schema-promotion-permanent">
                <Tooltip title={`Promoted master${effectiveVersion !== null ? ` at version ${effectiveVersion}` : ""} — deletion is locked (423)`}>
                    <Tag color="purple" className="m-0">
                        <LockOutlined /> PERMANENT{effectiveVersion !== null ? ` · V${effectiveVersion}` : ""}
                    </Tag>
                </Tooltip>
                <Button
                    size="small"
                    loading={!!working}
                    onClick={() =>
                        Modal.confirm({
                            title: `Demote '${schemaName}' back to VOLATILE?`,
                            content: "The schema becomes deletable again and loses its promoted-master lock.",
                            okText: "Demote",
                            okButtonProps: { danger: true },
                            onOk: () => dispatch(demoteSchemaRequest(businessAreaId, schemaId)),
                        })
                    }
                    data-testid="schema-demote-button"
                >
                    Demote
                </Button>
            </span>
        );
    }

    return (
        <Tooltip title="Lock this schema as the PERMANENT master definition — deletion will be blocked (423) and the current version is tagged as the promoted revision">
            <Button
                icon={<SafetyCertificateFilled />}
                loading={!!working}
                className="!border-purple-500 !text-purple-600"
                onClick={() =>
                    Modal.confirm({
                        title: `Promote '${schemaName}' to PERMANENT?`,
                        content: "The schema can no longer be deleted while PERMANENT, and the current version becomes the tagged promoted revision.",
                        okText: "Promote",
                        onOk: () => dispatch(promoteSchemaRequest(businessAreaId, schemaId)),
                    })
                }
                data-testid="schema-promote-button"
            >
                Promote to Permanent
            </Button>
        </Tooltip>
    );
}
